import { useEffect, useRef } from 'react';
import { useEvento } from '@/store/evento/evento';
import { apiUrl } from '@/lib/config';
import { reserveLifecycle } from './reserveLifecycle';

/**
 * Hook que libera la reserva cuando el usuario cierra la pestaña o navega fuera.
 * Usa sendBeacon porque es la única forma confiable de enviar datos en beforeunload.
 */
export const useBeforeUnloadRelease = () => {
    const { state: reservaId } = useEvento.use.reserva();
    const { state: funcion } = useEvento.use.funcion();

    // Refs para tener valores actuales en el event listener
    const reservaIdRef = useRef(reservaId);
    const funcionIdRef = useRef(funcion?.id);

    // Mantener refs actualizados
    useEffect(() => {
        reservaIdRef.current = reservaId;
        // Si reservaId cambia a null, resetear todos los flags para la próxima reserva
        if (!reservaId) {
            reserveLifecycle.reset();
        }
    }, [reservaId]);

    useEffect(() => {
        funcionIdRef.current = funcion?.id;
    }, [funcion?.id]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            // Redirección intencional a gateway externo (Allaria) — la reserva debe sobrevivir
            if (reserveLifecycle.is('skip-unload')) {
                return;
            }

            // Si ya se envió/está en progreso release desde useReserve, no duplicar
            if (reserveLifecycle.is('releasing')) {
                console.warn('[BeforeUnload] Release ya en progreso, ignorando');
                return;
            }

            const currentReservaId = reservaIdRef.current;
            const currentFuncionId = funcionIdRef.current;

            if (!currentReservaId || !currentFuncionId) {
                return;
            }

            // Marcar antes de enviar
            reserveLifecycle.set('releasing');

            const url = `${apiUrl('/eventos')}/release`;
            const data = JSON.stringify({
                funcionId: currentFuncionId,
                reservaId: currentReservaId
            });

            const blob = new Blob([data], { type: 'application/json' });
            navigator.sendBeacon(url, blob);

            console.warn('[BeforeUnload] Release enviado via sendBeacon');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);
};
